import type { DeskTicketRow, TicketFeedRow } from '../../lib/database.types'
import { WATCHDOG_STALE_MS } from './health'

/**
 * A department's open Linear tickets, as the watchdog last published them.
 *
 * Not `tickets.ts`, which groups agent runs by the ticket they belong to. This is
 * the other direction: tickets that may have no run at all, because the work is a
 * person's (the Data project).
 *
 * Linear is the source; this is a copy the watchdog replaces whole every ten
 * minutes (98h-infra sql/fleet/20260923200000_fleet_tickets.sql). So the mapper's
 * two jobs are the ones a copy needs: say how old it is, and not show a ticket
 * twice. A ticket attached to an open data finding is shown on its finding, so it
 * is left out here. Nothing on the list is a control -- a ticket is changed in
 * Linear, and the row links there.
 *
 * The staleness threshold is the watchdog's own: the list is published by the same
 * pass that proves the watchdog is alive, so it goes stale exactly when that does.
 */

export interface LinearTicket {
  ref: string
  title: string
  url: string
  state: string
  priority: number
  /** Linear's own word for the priority. 0 is "No priority", not "urgent". */
  priorityLabel: string
  assignee: string | null
  labels: string[]
  updatedAt: string | null
}

export interface LinearTicketList {
  tickets: LinearTicket[]
  /** Tickets left out because they are shown on an open finding. */
  onFindings: number
  /** When the watchdog last published this department's list; null if it never has. */
  asOf: string | null
  /** No publish inside the watchdog's liveness window: the list may be out of date. */
  stale: boolean
  /** The department has a feed at all. A desk without one renders no ticket section. */
  published: boolean
}

const PRIORITY_LABEL: Record<number, string> = { 0: 'No priority', 1: 'Urgent', 2: 'High', 3: 'Medium', 4: 'Low' }

export function priorityLabel(p: number): string {
  return PRIORITY_LABEL[p] ?? 'No priority'
}

export function toLinearTicketList(
  rows: ReadonlyArray<DeskTicketRow>,
  feeds: ReadonlyArray<TicketFeedRow>,
  now: number = Date.now(),
): LinearTicketList {
  const feed = feeds[0] ?? null
  const asOf = feed?.generated_at ?? null
  const at = asOf ? Date.parse(asOf) : NaN
  const tickets: LinearTicket[] = []
  let onFindings = 0
  for (const r of rows) {
    if (!r.ref || !r.title || !r.url) continue
    if (r.open_finding_id) {
      onFindings += 1
      continue
    }
    const priority = r.priority ?? 0
    tickets.push({
      ref: r.ref, title: r.title, url: r.url, state: r.state ?? '', priority,
      priorityLabel: priorityLabel(priority), assignee: r.assignee, labels: r.labels ?? [],
      updatedAt: r.linear_updated_at,
    })
  }
  // Urgent first, "No priority" last, then the most recently touched.
  const rank = (p: number) => (p === 0 ? 5 : p)
  tickets.sort((a, b) =>
    rank(a.priority) - rank(b.priority) || (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '') || a.ref.localeCompare(b.ref))
  return {
    tickets,
    onFindings,
    asOf,
    stale: feed !== null && (Number.isNaN(at) || now - at > WATCHDOG_STALE_MS),
    published: feed !== null,
  }
}
